/**
 * IndexedDB wrapper for caching order data in MultiCartShelfManager
 * Much better than localStorage - supports 50MB+ storage, async operations, and structured data
 */

class OrderCacheDB {
  constructor() {
    this.dbName = 'MultiCartShelfCache';
    this.dbVersion = 1;
    this.db = null;
  }

  /**
   * Initialize the database
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object store for individual orders
        if (!db.objectStoreNames.contains('orders')) {
          const orderStore = db.createObjectStore('orders', { keyPath: 'orderId' });
          orderStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('📦 Created "orders" object store');
        }

        // Create object store for bulk cache
        if (!db.objectStoreNames.contains('bulkCache')) {
          db.createObjectStore('bulkCache', { keyPath: 'id' });
          console.log('📦 Created "bulkCache" object store');
        }
      };
    });
  }

  /**
   * Get a single order from cache
   */
  async getOrder(orderId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['orders'], 'readonly');
      const store = transaction.objectStore('orders');
      const request = store.get(orderId);

      request.onsuccess = () => {
        const result = request.result;

        if (!result) {
          resolve(null);
          return;
        }

        // Check if cache expired (30 minutes)
        const cacheAge = Date.now() - result.timestamp;
        const maxAge = 30 * 60 * 1000;

        if (cacheAge > maxAge) {
          console.log(`⏰ Order ${orderId} cache expired, removing...`);
          this.deleteOrder(orderId);
          resolve(null);
        } else {
          resolve(result.data);
        }
      };

      request.onerror = () => {
        console.error('Error getting order:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Save a single order to cache
   */
  async saveOrder(orderId, data) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['orders'], 'readwrite');
      const store = transaction.objectStore('orders');

      const cacheEntry = {
        orderId: orderId,
        data: data,
        timestamp: Date.now()
      };

      const request = store.put(cacheEntry);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('Error saving order:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete a single order from cache
   */
  async deleteOrder(orderId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['orders'], 'readwrite');
      const store = transaction.objectStore('orders');
      const request = store.delete(orderId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get bulk cache (all orders loaded via "Alle Daten laden")
   */
  async getBulkCache() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['bulkCache'], 'readonly');
      const store = transaction.objectStore('bulkCache');
      const request = store.get('current');

      request.onsuccess = () => {
        const result = request.result;

        if (!result) {
          resolve(null);
          return;
        }

        // Check if cache expired (30 minutes)
        const cacheAge = Date.now() - result.timestamp;
        const maxAge = 30 * 60 * 1000;

        if (cacheAge > maxAge) {
          console.log('⏰ Bulk cache expired, removing...');
          this.deleteBulkCache();
          resolve(null);
        } else {
          const cacheSizeMB = (JSON.stringify(result.orderArticles).length / 1024 / 1024).toFixed(2);
          console.log(`✅ Bulk cache found: ${Object.keys(result.orderArticles).length} orders, ${cacheSizeMB} MB, ${Math.round(cacheAge / 60000)} min old`);
          resolve(result);
        }
      };

      request.onerror = () => {
        console.error('Error getting bulk cache:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Save bulk cache (all orders loaded via "Alle Daten laden")
   */
  async saveBulkCache(orderArticles) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['bulkCache'], 'readwrite');
      const store = transaction.objectStore('bulkCache');

      const cacheEntry = {
        id: 'current',
        orderArticles: orderArticles,
        timestamp: Date.now()
      };

      const request = store.put(cacheEntry);

      request.onsuccess = () => {
        const cacheSizeMB = (JSON.stringify(orderArticles).length / 1024 / 1024).toFixed(2);
        console.log(`✅ Bulk cache saved: ${Object.keys(orderArticles).length} orders, ${cacheSizeMB} MB`);
        resolve();
      };

      request.onerror = () => {
        console.error('Error saving bulk cache:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete bulk cache
   */
  async deleteBulkCache() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['bulkCache'], 'readwrite');
      const store = transaction.objectStore('bulkCache');
      const request = store.delete('current');

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clean up expired orders from cache
   */
  async cleanupExpiredOrders() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['orders'], 'readwrite');
      const store = transaction.objectStore('orders');
      const index = store.index('timestamp');
      const request = index.openCursor();

      const now = Date.now();
      const maxAge = 30 * 60 * 1000; // 30 minutes
      let removedCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;

        if (cursor) {
          const cacheAge = now - cursor.value.timestamp;

          if (cacheAge > maxAge) {
            cursor.delete();
            removedCount++;
          }

          cursor.continue();
        } else {
          // Done iterating
          if (removedCount > 0) {
            console.log(`🧹 Cleaned up ${removedCount} expired orders from IndexedDB`);
          }
          resolve(removedCount);
        }
      };

      request.onerror = () => {
        console.error('Error cleaning up expired orders:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all cached data
   */
  async clearAll() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['orders', 'bulkCache'], 'readwrite');

      const ordersStore = transaction.objectStore('orders');
      const bulkStore = transaction.objectStore('bulkCache');

      const clearOrders = ordersStore.clear();
      const clearBulk = bulkStore.clear();

      transaction.oncomplete = () => {
        console.log('🧹 Cleared all cached data from IndexedDB');
        resolve();
      };

      transaction.onerror = () => {
        console.error('Error clearing cache:', transaction.error);
        reject(transaction.error);
      };
    });
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['orders', 'bulkCache'], 'readonly');
      const ordersStore = transaction.objectStore('orders');
      const bulkStore = transaction.objectStore('bulkCache');

      const countOrders = ordersStore.count();
      const getBulk = bulkStore.get('current');

      const stats = {
        individualOrders: 0,
        hasBulkCache: false,
        bulkCacheOrders: 0,
        bulkCacheAge: null
      };

      countOrders.onsuccess = () => {
        stats.individualOrders = countOrders.result;
      };

      getBulk.onsuccess = () => {
        if (getBulk.result) {
          stats.hasBulkCache = true;
          stats.bulkCacheOrders = Object.keys(getBulk.result.orderArticles || {}).length;
          stats.bulkCacheAge = Math.round((Date.now() - getBulk.result.timestamp) / 60000);
        }
      };

      transaction.oncomplete = () => resolve(stats);
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

// Create singleton instance
const orderCacheDB = new OrderCacheDB();

export default orderCacheDB;
